/* eslint-disable react/no-unknown-property -- Firebolt потребляет css-проп при сборке. */

// VOICE ENHANCEMENT — очистка микрофона без изменения голоса.
// Пропы: { world, onClose }. ESC закрывает экран.
//
// Всё, что показывает экран, — настоящее: ползунки двигают реальные узлы
// Web Audio (src/client/voice-chain.js), волна и уровень берутся с
// анализаторов, A/B пишет два потока одновременно — сухой и обработанный.
// Ничего не имитируем: если микрофон не дали, экран говорит об этом прямо.

import { css } from '@firebolt-dev/css'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  PlayIcon,
  PauseIcon,
  SaveIcon,
  RotateCcwIcon,
  UploadIcon,
  ArrowLeftRightIcon,
  PowerIcon,
  ShieldCheckIcon,
  ActivityIcon,
  WavesIcon,
  FilterIcon,
  AudioLinesIcon,
  AudioWaveformIcon,
  Volume2Icon,
  SlidersHorizontalIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  Gamepad2Icon,
  WifiIcon,
  AlertTriangleIcon,
} from 'lucide-react'

import { COLORS, RADII, space, MOTION, SHADOWS, TYPE, ICON_SIZES, INPUT_HEIGHTS } from '../theme'
import {
  VOICE_PARAMS,
  VOICE_PRESETS,
  DEFAULT_SETTINGS,
  loadCustomPresets,
  saveCustomPreset,
  listMicrophones,
  formatParamValue,
  isRecommended,
  matchPreset,
  clampParam,
  renderFileThroughChain,
  bufferPeaks,
} from '../voice-chain'
import { getVoiceService } from '../voice-service'
import { ScreenShell, IdentityBadge, MetaStat, Panel, Btn, Toggle, Bars, ActionBar, SubLabel } from './VoiceKit'

const PARAM_ICONS = {
  noiseSuppression: AudioLinesIcon,
  echoReduction: WavesIcon,
  lowCut: FilterIcon,
  clarityEq: AudioWaveformIcon,
  warmth: WavesIcon,
  deEsser: AudioLinesIcon,
  compressor: SlidersHorizontalIcon,
  limiter: ActivityIcon,
  outputGain: Volume2Icon,
}

const RECORD_SECONDS = 10

// ─── Проигрыватель AudioBuffer: одинаково работает и для записи, и для файла ──

class BufferPlayer {
  constructor() {
    this.ctx = null
    this.source = null
    this.startedAt = 0
    this.buffer = null
    this.onEnded = null
  }

  play(buffer, onEnded) {
    this.stop()
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!this.ctx) this.ctx = new Ctx()
    if (this.ctx.state === 'suspended') this.ctx.resume()
    this.buffer = buffer
    this.onEnded = onEnded
    this.source = this.ctx.createBufferSource()
    this.source.buffer = buffer
    this.source.connect(this.ctx.destination)
    this.source.onended = () => {
      this.source = null
      onEnded?.()
    }
    this.startedAt = this.ctx.currentTime
    this.source.start()
  }

  get position() {
    if (!this.source || !this.ctx) return 0
    return Math.min(this.buffer?.duration || 0, this.ctx.currentTime - this.startedAt)
  }

  get playing() {
    return !!this.source
  }

  stop() {
    if (this.source) {
      try {
        this.source.onended = null
        this.source.stop()
      } catch {
        /* уже остановлен */
      }
      this.source = null
    }
  }

  dispose() {
    this.stop()
    try {
      this.ctx?.close?.()
    } catch {
      /* уже закрыт */
    }
    this.ctx = null
  }
}

function formatClock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0))
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

// ─── Экран ───────────────────────────────────────────────────────────────────

export function VoiceScreen({ world, onClose }) {
  // Тракт общий на весь клиент — экран им пользуется, но не владеет.
  const service = useMemo(() => getVoiceService(), [])

  const [settings, setSettings] = useState(service.settings)
  const [enabled, setEnabled] = useState(service.isPublishingEnabled())
  const [capture, setCapture] = useState(service.capture)
  const [deviceId, setDeviceId] = useState(service.deviceId)
  const [devices, setDevices] = useState([])
  const [customPresets, setCustomPresets] = useState(() => loadCustomPresets())
  const [showInputSettings, setShowInputSettings] = useState(false)

  const [micError, setMicError] = useState(null)
  const [ready, setReady] = useState(false)
  const [wave, setWave] = useState(() => new Array(48).fill(0))
  const [level, setLevel] = useState({ rmsDb: -100, peakDb: -100, linear: 0 })
  const [latency, setLatency] = useState(0)
  const [duck, setDuck] = useState(0)

  // A/B: две дорожки одного и того же материала
  const [samples, setSamples] = useState(null) // { original, enhanced, source }
  const [swapped, setSwapped] = useState(false)
  const [recording, setRecording] = useState(false)
  const [recordTime, setRecordTime] = useState(0)
  const [playing, setPlaying] = useState(null) // 'original' | 'enhanced' | null
  const [playPos, setPlayPos] = useState(0)
  const [busy, setBusy] = useState(null)
  const [saved, setSaved] = useState(null)

  const playerRef = useRef(null)
  const recordersRef = useRef([])
  const rafRef = useRef(0)
  const fileInputRef = useRef(null)

  // --- Берём общий тракт на время жизни экрана ---
  useEffect(() => {
    let cancelled = false
    playerRef.current = new BufferPlayer()

    service
      .acquire('voice-screen')
      .then(async () => {
        if (cancelled) return
        setReady(true)
        setLatency(service.chain.latencyMs)
        // метки устройств доступны только после выданного разрешения
        setDevices(await listMicrophones())
      })
      .catch(() => {
        if (cancelled) return
        setMicError(service.error || 'Микрофон недоступен.')
      })

    return () => {
      cancelled = true
      // тракт не останавливаем — он нужен голосу в мире после закрытия экрана
      service.release('voice-screen')
      playerRef.current?.dispose()
      recordersRef.current.forEach(rec => {
        try {
          if (rec.state !== 'inactive') rec.stop()
        } catch {
          /* уже остановлен */
        }
      })
    }
  }, [service])

  // --- Живая волна, уровень и позиция воспроизведения ---
  useEffect(() => {
    if (!ready) return undefined
    const tick = () => {
      const chain = service.chain
      if (chain?.ready) {
        setWave(chain.waveform(enabled ? 'enhanced' : 'original', 48))
        setLevel(chain.levels(enabled ? 'enhanced' : 'original'))
        setDuck(chain.duck)
      }
      const player = playerRef.current
      if (player?.playing) setPlayPos(player.position)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [ready, enabled, service])

  // --- ESC закрывает ---
  useEffect(() => {
    const onKey = event => {
      if (event.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // --- Ползунок → живой тракт ---
  const updateParam = useCallback(
    (id, value) => {
      const param = VOICE_PARAMS.find(p => p.id === id)
      setSettings(service.update({ ...service.settings, [id]: clampParam(param, value) }))
      setSaved(null)
    },
    [service]
  )

  const applyPreset = useCallback(
    preset => {
      setSettings(service.update(preset.values))
      setSaved(null)
    },
    [service]
  )

  const resetAll = useCallback(() => {
    setSettings(service.update(DEFAULT_SETTINGS))
    setSaved(null)
  }, [service])

  const toggleEnabled = useCallback(() => {
    setEnabled(service.setPublishingEnabledByUser(!service.isPublishingEnabled()))
    setSaved(null)
  }, [service])

  const changeDevice = useCallback(
    async id => {
      setDeviceId(id)
      setReady(false)
      try {
        await service.setDevice(id)
        setReady(true)
        setMicError(null)
      } catch (error) {
        setMicError(String(error?.message || error))
      }
    },
    [service]
  )

  const changeCapture = useCallback(
    async patch => {
      setCapture(await service.setCapture(patch))
      setSaved(null)
    },
    [service]
  )

  // --- Запись пробы: два потока одновременно ---
  const startRecording = useCallback(async () => {
    const chain = service.chain
    if (!chain?.ready || recording) return
    if (typeof MediaRecorder === 'undefined') {
      setMicError('Браузер не умеет MediaRecorder — проба недоступна.')
      return
    }
    playerRef.current?.stop()
    setPlaying(null)
    setSamples(null)
    setRecording(true)
    setRecordTime(0)

    const chunks = { original: [], enhanced: [] }
    const recorders = ['original', 'enhanced'].map(kind => {
      const stream = kind === 'original' ? chain.originalStream : chain.enhancedStream
      const rec = new MediaRecorder(stream)
      rec.ondataavailable = event => {
        if (event.data?.size) chunks[kind].push(event.data)
      }
      return { kind, rec }
    })
    recordersRef.current = recorders.map(item => item.rec)

    const finished = recorders.map(
      ({ kind, rec }) =>
        new Promise(resolve => {
          rec.onstop = async () => {
            const blob = new Blob(chunks[kind], { type: rec.mimeType || 'audio/webm' })
            try {
              const Ctx = window.AudioContext || window.webkitAudioContext
              const ctx = new Ctx()
              const buffer = await ctx.decodeAudioData(await blob.arrayBuffer())
              ctx.close?.()
              resolve([kind, buffer])
            } catch {
              resolve([kind, null])
            }
          }
        })
    )

    recorders.forEach(({ rec }) => rec.start())
    const startedAt = performance.now()
    const timer = setInterval(() => {
      const elapsed = (performance.now() - startedAt) / 1000
      setRecordTime(elapsed)
      if (elapsed >= RECORD_SECONDS) {
        clearInterval(timer)
        recorders.forEach(({ rec }) => rec.state !== 'inactive' && rec.stop())
      }
    }, 100)

    const results = Object.fromEntries(await Promise.all(finished))
    clearInterval(timer)
    recordersRef.current = []
    setRecording(false)
    setRecordTime(0)
    if (results.original && results.enhanced) {
      setSamples({ original: results.original, enhanced: results.enhanced, source: 'Проба голоса' })
    } else {
      setMicError('Не удалось разобрать запись пробы.')
    }
  }, [recording, service])

  const stopRecording = useCallback(() => {
    recordersRef.current.forEach(rec => {
      try {
        if (rec.state !== 'inactive') rec.stop()
      } catch {
        /* уже остановлен */
      }
    })
  }, [])

  // --- Импорт файла: тот же тракт, но оффлайн ---
  const importFile = useCallback(
    async file => {
      if (!file) return
      setBusy('import')
      setMicError(null)
      try {
        const arrayBuffer = await file.arrayBuffer()
        const { original, enhanced } = await renderFileThroughChain(arrayBuffer, settings)
        playerRef.current?.stop()
        setPlaying(null)
        setSamples({ original, enhanced, source: file.name })
      } catch (error) {
        setMicError(`Файл не разобрался: ${error?.message || error}`)
      } finally {
        setBusy(null)
      }
    },
    [settings]
  )

  // --- Воспроизведение A/B ---
  const togglePlay = useCallback(
    kind => {
      const player = playerRef.current
      if (!player || !samples) return
      if (playing === kind) {
        player.stop()
        setPlaying(null)
        setPlayPos(0)
        return
      }
      player.play(samples[kind], () => {
        setPlaying(null)
        setPlayPos(0)
      })
      setPlaying(kind)
    },
    [playing, samples]
  )

  // --- Сохранение ---
  const handleSave = useCallback(() => {
    const ok = service.save()
    setSaved(ok ? 'Настройки сохранены' : 'Браузер не дал сохранить (приватный режим)')
    setTimeout(() => setSaved(null), 3000)
  }, [service])

  const handleSavePreset = useCallback(() => {
    const label = window.prompt('Название пресета:', 'Мой пресет')
    if (!label) return
    setCustomPresets(saveCustomPreset(label, settings))
  }, [settings])

  const presets = useMemo(() => [...VOICE_PRESETS, ...customPresets], [customPresets])
  const activePreset = useMemo(() => matchPreset(settings, presets), [settings, presets])

  const peaks = useMemo(
    () => ({
      original: samples ? bufferPeaks(samples.original, 72) : null,
      enhanced: samples ? bufferPeaks(samples.enhanced, 72) : null,
    }),
    [samples]
  )

  const left = swapped ? 'enhanced' : 'original'
  const right = swapped ? 'original' : 'enhanced'

  return (
    <ScreenShell
      label='WORLD'
      title='VOICE ENHANCEMENT'
      subtitle='WITHOUT VOICE CHANGING'
      ariaLabel='Обработка голоса'
      onClose={onClose}
      meta={
        <>
          <IdentityBadge title='VOICE IDENTITY PRESERVED' subtitle='Твой голос, только чище' />
          <MetaStat
            icon={<ActivityIcon size={ICON_SIZES.sm} color={COLORS.cyan} />}
            label='ШУМОДАВ'
            value={enabled ? `${Math.round(duck * 100)}%` : 'выкл'}
          />
          <MetaStat
            icon={<WifiIcon size={ICON_SIZES.sm} color={COLORS.cyan} />}
            label='ЗАДЕРЖКА'
            value={latency ? `${latency.toFixed(0)} ms` : '—'}
          />
          <MetaStat
            icon={<Gamepad2Icon size={ICON_SIZES.sm} color={COLORS.textSecondary} />}
            label='МИР'
            value={world?.network?.id ? 'на связи' : 'офлайн'}
            tone={world?.network?.id ? 'success' : undefined}
          />
        </>
      }
    >
      <div
        css={css`
          flex: 1 1 auto;
          min-height: 0;
          display: grid;
          grid-template-columns: 360px 1fr 480px;
          gap: ${space(2)};
          padding: ${space(2)};

          @media (max-width: 1400px) {
            grid-template-columns: 320px 1fr 400px;
          }
          @media (max-width: 1100px) {
            grid-template-columns: 1fr;
            grid-auto-rows: min-content;
            overflow-y: auto;
          }
        `}
      >
        {/* ── ЛЕВО: микрофон и живой вход ─────────────────────────────── */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${space(2)};
            min-height: 0;
          `}
        >
          <Panel>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                padding-top: ${space(2)};
              `}
            >
              <div
                aria-hidden='true'
                css={css`
                  width: 96px;
                  height: 96px;
                  border-radius: ${RADII.round};
                  border: 2px solid ${COLORS.borderStrong};
                  background: radial-gradient(circle at 40% 30%, ${COLORS.cyanSoft}, ${COLORS.bgElevated});
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 2rem;
                  font-weight: 700;
                  color: ${COLORS.cyan};
                  box-shadow: ${SHADOWS.glowCyan};
                `}
              >
                M
              </div>
              <div
                css={css`
                  font-size: 1.3rem;
                  font-weight: 700;
                  letter-spacing: 0.12em;
                  margin-top: ${space(1)};
                `}
              >
                MIA
              </div>
              <div
                css={css`
                  font-size: ${TYPE.caption.size};
                  letter-spacing: 0.18em;
                  color: ${COLORS.textSecondary};
                `}
              >
                AI COMPANION
              </div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  margin-top: ${space(0)};
                  font-size: ${TYPE.caption.size};
                  color: ${COLORS.success};
                `}
              >
                <span
                  css={css`
                    width: 8px;
                    height: 8px;
                    border-radius: ${RADII.round};
                    background: ${COLORS.success};
                  `}
                />
                {world?.network?.id ? 'Online' : 'Offline'}
              </div>
            </div>
          </Panel>

          <Panel title='МИКРОФОН' info='Устройство захвата. Метки появляются после разрешения в браузере.'>
            <select
              value={deviceId}
              onChange={event => changeDevice(event.target.value)}
              css={css`
                width: 100%;
                min-height: ${INPUT_HEIGHTS.large}px;
                padding: 0 ${space(1)};
                border-radius: ${RADII.lg};
                border: 1px solid ${COLORS.border};
                background: ${COLORS.bgDeep};
                color: ${COLORS.text};
                font-family: inherit;
                font-size: ${TYPE.input.size};
                &:focus-visible {
                  outline: none;
                  box-shadow: ${SHADOWS.focus};
                }
              `}
            >
              <option value=''>Микрофон по умолчанию</option>
              {devices.map(device => (
                <option key={device.id} value={device.id}>
                  {device.label}
                </option>
              ))}
            </select>

            <button
              type='button'
              onClick={() => setShowInputSettings(v => !v)}
              css={css`
                appearance: none;
                width: 100%;
                margin-top: ${space(1)};
                padding: ${space(0)} 0;
                background: transparent;
                border: none;
                color: ${COLORS.textSecondary};
                font-family: inherit;
                font-size: ${TYPE.caption.size};
                display: flex;
                align-items: center;
                justify-content: space-between;
                cursor: pointer;
                &:hover {
                  color: ${COLORS.cyan};
                }
              `}
            >
              Настройки захвата
              {showInputSettings ? <ChevronDownIcon size={ICON_SIZES.xs} /> : <ChevronRightIcon size={ICON_SIZES.xs} />}
            </button>

            {showInputSettings && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: ${space(0)};
                  margin-top: ${space(0)};
                `}
              >
                <Toggle
                  checked={capture.echoCancellation}
                  onChange={v => changeCapture({ echoCancellation: v })}
                  label='ЭХОПОДАВЛЕНИЕ БРАУЗЕРА'
                  hint='Штатный AEC. Нужен, когда слушаешь колонки, а не наушники.'
                />
                <Toggle
                  checked={capture.noiseSuppression}
                  onChange={v => changeCapture({ noiseSuppression: v })}
                  label='ШУМОДАВ БРАУЗЕРА'
                  hint='Штатный NS. Работает вместе с нашим гейтом.'
                />
                <Toggle
                  checked={capture.autoGainControl}
                  onChange={v => changeCapture({ autoGainControl: v })}
                  label='АВТОГРОМКОСТЬ БРАУЗЕРА'
                  hint='По умолчанию выключена: дерётся с нашим компрессором.'
                />
              </div>
            )}
          </Panel>

          <Panel title='ЖИВОЙ ВХОД' info='Волна и уровень — с анализатора реального тракта.'>
            {micError ? (
              <div
                css={css`
                  display: flex;
                  gap: ${space(1)};
                  align-items: flex-start;
                  padding: ${space(1)};
                  border: 1px solid ${COLORS.errorSoft};
                  border-radius: ${RADII.lg};
                  background: ${COLORS.errorSoft};
                  font-size: ${TYPE.caption.size};
                  color: ${COLORS.text};
                `}
              >
                <AlertTriangleIcon size={ICON_SIZES.sm} color={COLORS.error} />
                <span>{micError}</span>
              </div>
            ) : (
              <>
                <Bars values={wave} color={enabled ? COLORS.success : COLORS.textSecondary} height={54} dim={!ready} />
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    margin-top: ${space(1)};
                    font-size: ${TYPE.caption.size};
                    letter-spacing: ${TYPE.caption.spacing};
                    color: ${COLORS.textSecondary};
                  `}
                >
                  <span>УРОВЕНЬ ВХОДА</span>
                  <span
                    css={css`
                      color: ${level.rmsDb > -12 ? COLORS.warning : COLORS.text};
                    `}
                  >
                    {level.rmsDb <= -99 ? '—' : `${level.rmsDb.toFixed(0)} dB`}
                  </span>
                </div>
                <DbMeter rmsDb={level.rmsDb} peakDb={level.peakDb} />
              </>
            )}
          </Panel>

          <div
            css={css`
              display: flex;
              gap: ${space(1)};
              align-items: flex-start;
              padding: ${space(1)};
              border: 1px solid ${COLORS.successSoft};
              border-radius: ${RADII.lg};
              background: ${COLORS.successSoft};
              font-size: ${TYPE.caption.size};
              color: ${COLORS.textSecondary};
            `}
          >
            <ShieldCheckIcon size={ICON_SIZES.sm} color={COLORS.success} />
            <span>Это очистка, а не подмена голоса. Мы сохраняем твою естественную окраску и тембр.</span>
          </div>
        </div>

        {/* ── ЦЕНТР: A/B и проба ──────────────────────────────────────── */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${space(2)};
            min-height: 0;
          `}
        >
          <Panel
            title='A / B СРАВНЕНИЕ'
            info='Один и тот же материал: слева сухой, справа обработанный.'
            right={
              <Btn size='small' variant='ghost' onClick={() => setSwapped(v => !v)} disabled={!samples}>
                <ArrowLeftRightIcon size={ICON_SIZES.xs} />
                МЕСТАМИ
              </Btn>
            }
          >
            <div
              css={css`
                display: grid;
                grid-template-columns: 1fr auto 1fr;
                gap: ${space(1)};
                align-items: stretch;

                @media (max-width: 700px) {
                  grid-template-columns: 1fr;
                }
              `}
            >
              <ABCard
                tag='A'
                title={left === 'original' ? 'ОРИГИНАЛ' : 'ОБРАБОТАННЫЙ'}
                hint={left === 'original' ? 'Микрофон как есть' : 'Чище и разборчивее'}
                peaks={peaks[left]}
                color={left === 'original' ? COLORS.textSecondary : COLORS.success}
                playing={playing === left}
                position={playing === left ? playPos : 0}
                duration={samples?.[left]?.duration || 0}
                onToggle={() => togglePlay(left)}
                disabled={!samples}
              />
              <div
                aria-hidden='true'
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: ${COLORS.textSecondary};
                  @media (max-width: 700px) {
                    display: none;
                  }
                `}
              >
                <ChevronRightIcon size={ICON_SIZES.md} />
              </div>
              <ABCard
                tag='B'
                title={right === 'original' ? 'ОРИГИНАЛ' : 'ОБРАБОТАННЫЙ'}
                hint={right === 'original' ? 'Микрофон как есть' : 'Чище и разборчивее'}
                peaks={peaks[right]}
                color={right === 'original' ? COLORS.textSecondary : COLORS.success}
                playing={playing === right}
                position={playing === right ? playPos : 0}
                duration={samples?.[right]?.duration || 0}
                onToggle={() => togglePlay(right)}
                disabled={!samples}
              />
            </div>
          </Panel>

          <Panel title='ПРОБА' info={`Пишем ${RECORD_SECONDS} секунд обоих потоков одновременно.`}>
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: ${space(2)};
                flex-wrap: wrap;
              `}
            >
              <div
                css={css`
                  flex: 1 1 200px;
                  font-size: ${TYPE.caption.size};
                  color: ${COLORS.textSecondary};
                `}
              >
                Запиши короткую пробу, чтобы услышать разницу. Говори как обычно, не громче.
              </div>

              <button
                type='button'
                aria-label={recording ? 'Остановить запись' : 'Записать пробу'}
                onClick={recording ? stopRecording : startRecording}
                disabled={!ready || !!busy}
                css={css`
                  appearance: none;
                  width: 52px;
                  height: 52px;
                  border-radius: ${RADII.round};
                  border: 2px solid ${recording ? COLORS.error : COLORS.border};
                  background: ${recording ? COLORS.error : COLORS.errorSoft};
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  transition: transform ${MOTION.hover};
                  &:hover:not(:disabled) {
                    transform: scale(1.05);
                  }
                  &:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                  }
                  &:focus-visible {
                    outline: none;
                    box-shadow: ${SHADOWS.focus};
                  }
                `}
              >
                <span
                  css={css`
                    width: ${recording ? '16px' : '20px'};
                    height: ${recording ? '16px' : '20px'};
                    border-radius: ${recording ? RADII.xs : RADII.round};
                    background: ${recording ? COLORS.text : COLORS.error};
                  `}
                />
              </button>

              <div
                css={css`
                  font-variant-numeric: tabular-nums;
                  font-size: 1rem;
                  min-width: 56px;
                  color: ${recording ? COLORS.error : COLORS.textSecondary};
                `}
              >
                {formatClock(recording ? recordTime : samples?.original?.duration || 0)}
              </div>

              <Btn onClick={() => fileInputRef.current?.click()} disabled={!!busy || recording}>
                <UploadIcon size={ICON_SIZES.xs} />
                {busy === 'import' ? 'ОБРАБОТКА…' : 'ЗАГРУЗИТЬ ФАЙЛ'}
              </Btn>
              <input
                ref={fileInputRef}
                type='file'
                accept='audio/*'
                onChange={event => {
                  importFile(event.target.files?.[0])
                  event.target.value = ''
                }}
                css={css`
                  display: none;
                `}
              />
            </div>

            {samples?.source && (
              <div
                css={css`
                  margin-top: ${space(1)};
                  font-size: ${TYPE.caption.size};
                  color: ${COLORS.textSecondary};
                `}
              >
                Источник: {samples.source}
              </div>
            )}
          </Panel>

          <Panel title='НА ЧТО СЛУШАТЬ'>
            <div
              css={css`
                font-size: ${TYPE.caption.size};
                color: ${COLORS.textSecondary};
                line-height: 1.6;
              `}
            >
              Разборчивость согласных, фон между фразами, естественность голоса. Если после обработки голос звучит
              «не как ты» — убавь Clarity EQ и Compressor: цель тракта не изменить тебя, а убрать лишнее.
            </div>
          </Panel>
        </div>

        {/* ── ПРАВО: параметры обработки ──────────────────────────────── */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${space(2)};
            min-height: 0;
          `}
        >
          <Panel
            title='ПАРАМЕТРЫ ОБРАБОТКИ'
            info='Каждый ползунок двигает реальный узел Web Audio.'
            right={
              <Btn size='small' variant='ghost' onClick={resetAll}>
                <RotateCcwIcon size={ICON_SIZES.xs} />
                СБРОС
              </Btn>
            }
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${space(1)};
              `}
            >
              {VOICE_PARAMS.map(param => (
                <ParamRow
                  key={param.id}
                  param={param}
                  value={settings[param.id]}
                  disabled={!enabled}
                  onChange={value => updateParam(param.id, value)}
                />
              ))}
            </div>
          </Panel>

          <Panel title='ПРЕСЕТЫ'>
            <div
              css={css`
                display: flex;
                flex-wrap: wrap;
                gap: ${space(0)};
              `}
            >
              {presets.map(preset => (
                <Btn
                  key={preset.id}
                  size='small'
                  variant={activePreset?.id === preset.id ? 'accent' : 'secondary'}
                  title={preset.hint}
                  onClick={() => applyPreset(preset)}
                >
                  {preset.label}
                </Btn>
              ))}
              {!activePreset && (
                <Btn size='small' variant='accent' disabled>
                  CUSTOM
                </Btn>
              )}
            </div>
          </Panel>
        </div>
      </div>

      <ActionBar
        left={
          <Btn variant={enabled ? 'destructive' : 'success'} onClick={toggleEnabled}>
            <PowerIcon size={ICON_SIZES.xs} />
            {enabled ? 'ОТКЛЮЧИТЬ ОБРАБОТКУ' : 'ВКЛЮЧИТЬ ОБРАБОТКУ'}
          </Btn>
        }
        center={
          <>
            <ShieldCheckIcon size={ICON_SIZES.xs} color={COLORS.success} />
            <span>{saved || 'Голос сохраняется как есть. Мы не клонируем, не синтезируем и не подменяем его.'}</span>
          </>
        }
        right={
          <>
            <Btn onClick={handleSavePreset}>
              <SaveIcon size={ICON_SIZES.xs} />
              СОХРАНИТЬ ПРЕСЕТ
            </Btn>
            <Btn variant='primary' onClick={handleSave}>
              СОХРАНИТЬ НАСТРОЙКИ
            </Btn>
          </>
        }
      />
    </ScreenShell>
  )
}

// ─── Строка параметра ────────────────────────────────────────────────────────

function ParamRow({ param, value, onChange, disabled }) {
  const Icon = PARAM_ICONS[param.id] || SlidersHorizontalIcon
  const ok = isRecommended(param, value)
  return (
    <div
      css={css`
        display: grid;
        grid-template-columns: 32px 1fr 78px;
        align-items: center;
        gap: ${space(1)};
        opacity: ${disabled ? 0.45 : 1};
      `}
    >
      <span
        css={css`
          width: 32px;
          height: 32px;
          border-radius: ${RADII.lg};
          border: 1px solid ${COLORS.border};
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${COLORS.cyan};
        `}
      >
        <Icon size={ICON_SIZES.xs} />
      </span>

      <span
        css={css`
          min-width: 0;
        `}
      >
        <span
          css={css`
            display: flex;
            align-items: baseline;
            gap: ${space(0)};
            flex-wrap: wrap;
          `}
        >
          <span
            css={css`
              font-size: 0.76rem;
              font-weight: 600;
            `}
          >
            {param.label}
          </span>
          <span
            css={css`
              font-size: 0.62rem;
              color: ${COLORS.textSecondary};
            `}
            title={param.does}
          >
            (рекомендуем: {param.recLabel})
          </span>
        </span>
        <input
          type='range'
          min={param.min}
          max={param.max}
          step={param.step}
          value={value}
          disabled={disabled}
          aria-label={`${param.label} — ${param.ru}`}
          onChange={event => onChange(Number(event.target.value))}
          css={css`
            width: 100%;
            margin-top: 4px;
            accent-color: ${ok ? COLORS.cyan : COLORS.warning};
            cursor: ${disabled ? 'not-allowed' : 'pointer'};
            &:focus-visible {
              outline: none;
              box-shadow: ${SHADOWS.focus};
            }
          `}
        />
      </span>

      <span
        css={css`
          text-align: right;
          font-size: 0.78rem;
          font-variant-numeric: tabular-nums;
          color: ${ok ? COLORS.text : COLORS.warning};
        `}
        title={ok ? param.does : `За рекомендованным диапазоном (${param.recLabel}). ${param.does}`}
      >
        {formatParamValue(param, value)}
      </span>
    </div>
  )
}

// ─── Карточка A/B ────────────────────────────────────────────────────────────

function ABCard({ tag, title, hint, peaks, color, playing, position, duration, onToggle, disabled }) {
  return (
    <div
      css={css`
        border: 1px solid ${COLORS.border};
        border-radius: ${RADII.lg};
        padding: ${space(1)};
        display: flex;
        flex-direction: column;
        gap: ${space(1)};
        background: ${COLORS.bgDeep};
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${space(0)};
        `}
      >
        <span
          css={css`
            width: 22px;
            height: 22px;
            border-radius: ${RADII.round};
            border: 1px solid ${COLORS.borderStrong};
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.7rem;
            font-weight: 700;
            color: ${COLORS.cyan};
          `}
        >
          {tag}
        </span>
        <span
          css={css`
            font-size: 0.76rem;
            font-weight: 650;
            letter-spacing: 0.1em;
          `}
        >
          {title}
        </span>
      </div>
      <div
        css={css`
          font-size: ${TYPE.caption.size};
          color: ${COLORS.textSecondary};
        `}
      >
        {hint}
      </div>

      <div
        css={css`
          flex: 1 1 auto;
          min-height: 96px;
          display: flex;
          align-items: center;
          border: 1px solid ${COLORS.border};
          border-radius: ${RADII.md};
          padding: ${space(0)};
          position: relative;
        `}
      >
        {peaks ? (
          <>
            <Bars values={peaks} color={color} height={84} />
            {playing && duration > 0 && (
              <span
                aria-hidden='true'
                css={css`
                  position: absolute;
                  top: 4px;
                  bottom: 4px;
                  left: ${(position / duration) * 100}%;
                  width: 2px;
                  background: ${COLORS.cyan};
                `}
              />
            )}
          </>
        ) : (
          <div
            css={css`
              width: 100%;
              text-align: center;
              font-size: ${TYPE.caption.size};
              color: ${COLORS.textSecondary};
            `}
          >
            Запиши пробу или загрузи файл
          </div>
        )}
      </div>

      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${space(1)};
        `}
      >
        <Btn size='small' variant='secondary' onClick={onToggle} disabled={disabled}>
          {playing ? <PauseIcon size={ICON_SIZES.xs} /> : <PlayIcon size={ICON_SIZES.xs} />}
        </Btn>
        <span
          css={css`
            font-size: ${TYPE.caption.size};
            font-variant-numeric: tabular-nums;
            color: ${COLORS.textSecondary};
          `}
        >
          {formatClock(position)} / {formatClock(duration)}
        </span>
      </div>
    </div>
  )
}

// ─── Шкала уровня в dBFS ─────────────────────────────────────────────────────

function DbMeter({ rmsDb, peakDb }) {
  const toPct = db => Math.max(0, Math.min(1, (Math.max(-60, Math.min(0, db)) + 60) / 60)) * 100
  const rms = toPct(rmsDb)
  const peak = toPct(peakDb)
  return (
    <div
      css={css`
        margin-top: ${space(0)};
      `}
    >
      <div
        css={css`
          position: relative;
          height: 14px;
          border-radius: ${RADII.xs};
          border: 1px solid ${COLORS.border};
          overflow: hidden;
          background: linear-gradient(
            to right,
            ${COLORS.success} 0%,
            ${COLORS.success} 80%,
            ${COLORS.warning} 80%,
            ${COLORS.warning} 90%,
            ${COLORS.error} 90%
          );
        `}
      >
        {/* гасим всё правее текущего уровня */}
        <div
          css={css`
            position: absolute;
            top: 0;
            bottom: 0;
            left: ${rms}%;
            right: 0;
            background: ${COLORS.bgDeep};
            opacity: 0.82;
            transition: left 0.08s linear;
          `}
        />
        <div
          aria-hidden='true'
          css={css`
            position: absolute;
            top: 0;
            bottom: 0;
            left: ${peak}%;
            width: 2px;
            background: ${COLORS.text};
          `}
        />
      </div>
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          margin-top: 3px;
          font-size: 0.58rem;
          color: ${COLORS.textSecondary};
        `}
      >
        <span>-60</span>
        <span>-40</span>
        <span>-20</span>
        <span>-6</span>
        <span>0</span>
      </div>
      <SubLabel>Белая метка — пик. Красная зона — перегруз.</SubLabel>
    </div>
  )
}
